import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core'
import PropTypes from 'prop-types'
import React from 'react'

/**
 * The session plan of a module, one row per session (owner, 2026-10-08): ops
 * set how many sessions the module has and give each a title, a concept and a
 * description. Writes `sessions` ([{n, name, concept, description}]) and
 * `sessions_count` into the form values.
 *
 * `explores` is still ON the row and still round-trips through
 * `formToPayload`; it simply has no column any more, because nothing draws it
 * to a parent. See the note on COLS.
 *
 * CONCEPT IS A COLUMN as of 2026-10-14. It was stored and shown to parents but
 * editable nowhere: the card in My classes reads "Pet feeder (part 1 of 2) ·
 * Gravity", and "Gravity" lived only in the JSON. Ops could see the line in the
 * app and had no way to change it. `description`, meanwhile, is the field this
 * form DID expose and the card never reads -- so the two have been the wrong
 * way round since the editor was simplified.
 *
 * GROUPING IS ON THE SESSION TITLE (owner, 2026-10-22). Consecutive rows that
 * share a title are one item in the parent's list instead of N, and the
 * parent app strips a "(part N of M)" tail before comparing -- so "Pet feeder
 * (part 1 of 2)" and "Pet feeder (part 2 of 2)" group, and so do two rows both
 * typed "Pet feeder". Give a row its own title and it stands alone.
 *
 * The TOPIC column went with this. It was a second, parallel way to say the
 * same thing (`groupSessions` has always had both paths), and it carried the
 * cost of a field whose whole purpose was invisible on this screen: a typo in
 * it silently ungrouped a session, which is the bug it kept producing. No
 * module on prod ever used it -- 0 of 12, checked before it was removed -- so
 * nothing a parent sees changes.
 */

// [key, column heading, { width, multiline, list }].
const COLS = [
  ['name', 'Session title', { width: 180 }],
  // CONCEPT IS BACK (owner, 2026-10-15). It was taken out when the parent app
  // stopped printing it; the design now shows it as the CONCEPT chip on the
  // session row -- "CONCEPT Steady beat - Naming all seven white keys". The
  // stored values survived the removal, so this column repopulates rather than
  // starting blank.
  ['concept', 'Concept', { multiline: true }],
  ['description', 'Session description', { multiline: true }]
  // NO "What they explore" (owner, 2026-10-22). The parent app stopped drawing
  // it on the session card the same day, so the column was asking ops to write
  // something nobody would read.
  //
  // THE STORED VALUES SURVIVE. `explores` is still mapped in `formToPayload`
  // (Setup.js), which is what keeps it on a row that is saved from this form --
  // `sessions` is written WHOLE, so dropping it from the payload is what would
  // erase it, not dropping the column. Same as `concept`, which came back in
  // October with its values intact after a spell with no column.
]

const SessionPlanField = ({ values, setField }) => {
  const count = Math.max(1, Math.min(52, Number(values.sessions_count) || 8))
  const rows = values.sessions || []
  const byN = Object.fromEntries(rows.map((r) => [r.n, r]))
  const numbers = Array.from({ length: count }, (_, i) => i + 1)
  const update = (n, key, value) => {
    const next = rows.filter((r) => r.n !== n)
    const row = {
      n,
      name: '',
      smart_name: '',
      helps: '',
      ...byN[n],
      [key]: value
    }
    setField(
      'sessions',
      [...next, row].sort((a, b) => a.n - b.n)
    )
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          gap: 16,
          alignItems: 'center',
          flexWrap: 'wrap'
        }}
      >
        <TextField
          type="number"
          label="Number of sessions"
          value={values.sessions_count ?? 8}
          onChange={(e) => setField('sessions_count', e.target.value)}
          variant="outlined"
          size="small"
          inputProps={{ min: 1, max: 52 }}
          style={{ width: 180 }}
        />
      </div>
      {/* The one rule you cannot guess from the headings: what makes two rows
          one item for the parent. Everything else a column already answers. */}
      <Typography variant="caption" color="textSecondary">
        One row per session. Give two or more the same Session title and the
        parent sees them as one item; a title of its own means the session
        stands on its own.
      </Typography>
      <div style={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell style={{ whiteSpace: 'nowrap' }}>#</TableCell>
              {COLS.map(([k, label]) => (
                <TableCell key={k}>{label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {numbers.map((n) => (
              <TableRow key={n}>
                <TableCell style={{ whiteSpace: 'nowrap' }}>{n}</TableCell>
                {COLS.map(([k, , opts = {}]) => (
                  <TableCell
                    key={k}
                    style={{
                      minWidth: opts.width || 320
                    }}
                  >
                    <TextField
                      value={byN[n]?.[k] || ''}
                      onChange={(e) => update(n, k, e.target.value)}
                      size="small"
                      fullWidth
                      placeholder={k === 'name' ? 'Required' : ''}
                      multiline={Boolean(opts.multiline)}
                      inputProps={opts.list ? { list: opts.list } : undefined}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

SessionPlanField.propTypes = {
  values: PropTypes.object.isRequired,
  setField: PropTypes.func.isRequired
}

export default SessionPlanField
